import { Box, Dialog, IconButton, Typography, alpha } from "@mui/material";
import { Close, ErrorOutline, WifiOff } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { Button } from "./Button";

/** Textos por tipo de error: los mismos de `ModalErrors` (legacy). */
const ERROR_COPY = {
  network: {
    title: "¡Ups!",
    body: "Parece que tu conexión a internet está fallando.",
    footer: "Verifica tu conexión Wi-Fi o intenta recargar la página.",
  },
  server: {
    title: "Una disculpa, algo salió mal",
    body: "Parece que nuestro servidor no responde, seguramente el equipo ya lo está resolviendo.",
    footer: "Inténtalo nuevamente más tarde.",
  },
} as const;

/**
 * Modal de error del wizard v2. Lee el mismo estado que `ModalErrors` (`app.modal_error`, que se
 * abre con `setModalErrorVisible`), así que las pantallas no cambian nada: solo se monta en los
 * layouts v2 en lugar del modal legacy de pantalla completa.
 */
export const DialogErrorV2 = () => {
  const dispatch = useAppDispatch();
  const modalError = useSelector((state: RootState) => state.app.modal_error);

  const isNetwork = modalError?.type === "FETCH_ERROR";
  const copy = isNetwork ? ERROR_COPY.network : ERROR_COPY.server;

  const handleClose = () => dispatch(setModalErrorVisible({ open: false, error: null }));

  return (
    <Dialog
      open={Boolean(modalError?.visible)}
      onClose={handleClose}
      PaperProps={{ sx: { width: "100%", maxWidth: 440, m: 2, borderRadius: "14px" } }}
    >
      <IconButton
        aria-label="Cerrar"
        onClick={handleClose}
        data-testid="dialog-error-close-icon"
        sx={{ position: "absolute", top: 10, right: 10, color: "text.secondary" }}
      >
        <Close fontSize="small" />
      </IconButton>

      <Box sx={{ p: "32px 28px 24px", textAlign: "center" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            mx: "auto",
            mb: 2,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "error.main",
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
          }}
        >
          {isNetwork ? <WifiOff /> : <ErrorOutline />}
        </Box>

        <Typography sx={{ fontSize: 19, fontWeight: 600, color: "text.primary", letterSpacing: "-0.01em" }}>
          {copy.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
          {copy.body}
        </Typography>
        <Typography variant="body3" component="p" color="text.secondary" sx={{ mt: 1.5 }}>
          {copy.footer}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 3 }}>
          {isNetwork && (
            <Button tone="outline" onClick={() => window.location.reload()} data-testid="dialog-error-reload-button">
              Recargar página
            </Button>
          )}
          <Button tone="primary" onClick={handleClose} data-testid="dialog-error-close-button">
            Entendido
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
